// ═══════════════════════════════════════════════════════════════
// CohabitationEditModal.js — Création / édition cohabitation (Family P2c)
// ═══════════════════════════════════════════════════════════════
// Deux modes :
//   - Création : saisie label + is_primary
//   - Édition : label + is_primary + membres (ajouter/retirer,
//     share_ratio par membre)
//
// Rappel règle : share_ratio ∈ [0,1]. Somme non contrainte par la
// DB, affichée en indication. Une seule cohabitation primaire par
// projet (le backend renvoie 409 sinon).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';
import { X, Save, Loader2, Trash2, Plus, UserPlus } from 'lucide-react';
import {
    createCohabitation,
    updateCohabitation,
    deleteCohabitation,
    addCohabitationMember,
    removeCohabitationMember,
} from '../../services/familyApi';
import { useTheme } from '../../contexts/ThemeContext';
import { toast, confirm } from '../ui/Toast';

const CohabitationEditModal = ({
    projectId,
    cohabitation,    // null = création
    persons = [],
    open,
    onClose,
    onSaved,
}) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const isEdit = !!cohabitation;

    const [label, setLabel] = useState('');
    const [isPrimary, setIsPrimary] = useState(false);
    const [members, setMembers] = useState([]);  // local view, re-fetch after save
    const [saving, setSaving] = useState(false);
    const [addPersonId, setAddPersonId] = useState('');
    const [addRole, setAddRole] = useState('');
    const [addShare, setAddShare] = useState('');

    useEffect(() => {
        if (!open) return;
        setLabel(cohabitation?.label || '');
        setIsPrimary(!!cohabitation?.is_primary);
        setMembers(cohabitation?.members || []);
        setAddPersonId('');
        setAddRole('');
        setAddShare('');
    }, [open, cohabitation]);

    const availablePersons = useMemo(() => {
        const memberIds = new Set(members.map((m) => m.person_id));
        return persons.filter((p) => !memberIds.has(p.id));
    }, [persons, members]);

    const personById = useMemo(
        () => Object.fromEntries(persons.map((p) => [p.id, p])),
        [persons]
    );

    const shareSum = useMemo(
        () => members.reduce((acc, m) => acc + (Number(m.share_ratio) || 0), 0),
        [members]
    );

    const handleSaveHeader = async () => {
        if (!label.trim()) {
            toast.error('Libellé requis');
            return;
        }
        setSaving(true);
        try {
            const patch = { label: label.trim(), is_primary: isPrimary };
            const result = isEdit
                ? await updateCohabitation(projectId, cohabitation.id, patch)
                : await createCohabitation(projectId, { label: label.trim(), isPrimary });
            toast.success(isEdit ? 'Cohabitation mise à jour' : 'Cohabitation créée');
            onSaved?.(result);
            if (!isEdit) onClose?.();
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleAddMember = async () => {
        if (!addPersonId) {
            toast.error('Choisir une personne');
            return;
        }
        const ratioNum = addShare ? parseFloat(addShare) : null;
        if (ratioNum != null && (isNaN(ratioNum) || ratioNum < 0 || ratioNum > 1)) {
            toast.error('Part entre 0 et 1 (ex : 0.5)');
            return;
        }
        setSaving(true);
        try {
            await addCohabitationMember(projectId, cohabitation.id, {
                personId: addPersonId,
                role: addRole.trim() || null,
                shareRatio: ratioNum,
            });
            toast.success('Membre ajouté');
            setMembers((prev) => [
                ...prev,
                { person_id: addPersonId, role_in_cohabitation: addRole.trim() || null, share_ratio: ratioNum },
            ]);
            setAddPersonId('');
            setAddRole('');
            setAddShare('');
            onSaved?.(cohabitation);
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveMember = async (personId) => {
        const name = personById[personId]?.full_name || 'cette personne';
        const ok = await confirm(`Retirer « ${name} » de la cohabitation ?`, { confirmText: 'Retirer' });
        if (!ok) return;
        setSaving(true);
        try {
            await removeCohabitationMember(projectId, cohabitation.id, personId);
            setMembers((prev) => prev.filter((m) => m.person_id !== personId));
            toast.success('Membre retiré');
            onSaved?.(cohabitation);
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!isEdit) return;
        const ok = await confirm(
            `Supprimer la cohabitation « ${cohabitation.label} » ? Les personnes ne sont pas supprimées.`,
            { danger: true, confirmText: 'Supprimer' }
        );
        if (!ok) return;
        setSaving(true);
        try {
            await deleteCohabitation(projectId, cohabitation.id);
            toast.success('Cohabitation supprimée');
            onSaved?.(null);
            onClose?.();
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    const surface = isDark ? '#1a1a2e' : '#ffffff';
    const surfaceAlt = isDark ? '#16162a' : '#f8fafc';
    const textPrimary = isDark ? '#e5e7eb' : '#1e293b';
    const textSecondary = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? '#2a2a44' : '#e2e8f0';
    const inputStyle = {
        padding: '7px 10px',
        background: surfaceAlt,
        color: textPrimary,
        border: `1px solid ${border}`,
        borderRadius: 8,
        fontSize: 12,
        outline: 'none',
        fontFamily: 'inherit',
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1100,
                background: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(15,23,42,0.5)',
                backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: surface, color: textPrimary, borderRadius: 14,
                    width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
                    border: `1px solid ${border}`,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{
                    padding: '18px 22px', borderBottom: `1px solid ${border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {isEdit ? 'Modifier la cohabitation' : 'Nouvelle cohabitation'}
                        </div>
                        <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
                            Groupe de vie partagée — pas exclusif, une personne peut appartenir à plusieurs cohabitations.
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'transparent', border: 'none', color: textSecondary, cursor: 'pointer', padding: 4, borderRadius: 6 }}
                    >
                        <X size={20} />
                    </button>
                </div>

                <div style={{ padding: 22, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Header fields */}
                    <div>
                        <Label textSecondary={textSecondary}>Libellé *</Label>
                        <input
                            type="text"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="ex : Foyer Paris, Résidence été, Colocation étudiante"
                            style={{ ...inputStyle, width: '100%', fontSize: 13 }}
                            autoFocus
                        />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={isPrimary}
                            onChange={(e) => setIsPrimary(e.target.checked)}
                            style={{ width: 15, height: 15 }}
                        />
                        <span style={{ fontSize: 12 }}>
                            Cohabitation primaire (une seule par projet)
                        </span>
                    </label>

                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                            onClick={handleSaveHeader}
                            disabled={saving}
                            style={{
                                background: '#1D4ED8', color: '#fff', border: 'none', borderRadius: 8,
                                padding: '7px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 6,
                                opacity: saving ? 0.65 : 1,
                            }}
                        >
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                            {isEdit ? 'Mettre à jour' : 'Créer'}
                        </button>
                    </div>

                    {/* Members (édition uniquement) */}
                    {isEdit && (
                        <>
                            <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16 }}>
                                <div style={{
                                    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
                                    color: textSecondary, marginBottom: 10,
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                                }}>
                                    <span>Membres ({members.length})</span>
                                    <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>
                                        Somme parts : {Math.round(shareSum * 100)}%
                                    </span>
                                </div>

                                {members.length === 0 ? (
                                    <div style={{ fontSize: 12, color: textSecondary, fontStyle: 'italic' }}>
                                        Aucun membre dans cette cohabitation.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {members.map((m) => {
                                            const p = personById[m.person_id];
                                            return (
                                                <div
                                                    key={m.person_id}
                                                    style={{
                                                        padding: '8px 10px', background: surfaceAlt,
                                                        border: `1px solid ${border}`, borderRadius: 8,
                                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    }}
                                                >
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <div style={{ fontSize: 12, fontWeight: 500 }}>
                                                            {p?.full_name || `Personne #${m.person_id}`}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                                            {m.role_in_cohabitation && <>{m.role_in_cohabitation}</>}
                                                            {m.role_in_cohabitation && m.share_ratio != null && ' · '}
                                                            {m.share_ratio != null && `${Math.round(m.share_ratio * 100)}%`}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleRemoveMember(m.person_id)}
                                                        disabled={saving}
                                                        style={{
                                                            background: 'transparent', color: '#ef4444',
                                                            border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6,
                                                        }}
                                                        title="Retirer de la cohabitation"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Add member form */}
                                {availablePersons.length > 0 && (
                                    <div style={{
                                        marginTop: 12, padding: 12, background: surfaceAlt,
                                        border: `1px dashed ${border}`, borderRadius: 8,
                                        display: 'grid', gridTemplateColumns: '1.5fr 1fr 80px auto', gap: 8, alignItems: 'end',
                                    }}>
                                        <div>
                                            <Label textSecondary={textSecondary}>Personne</Label>
                                            <select
                                                value={addPersonId}
                                                onChange={(e) => setAddPersonId(e.target.value)}
                                                style={{ ...inputStyle, width: '100%' }}
                                            >
                                                <option value="">— choisir —</option>
                                                {availablePersons.map((p) => (
                                                    <option key={p.id} value={p.id}>{p.full_name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <Label textSecondary={textSecondary}>Rôle (optionnel)</Label>
                                            <input
                                                type="text"
                                                value={addRole}
                                                onChange={(e) => setAddRole(e.target.value)}
                                                placeholder="Chef, Conjoint…"
                                                style={{ ...inputStyle, width: '100%' }}
                                            />
                                        </div>
                                        <div>
                                            <Label textSecondary={textSecondary}>Part</Label>
                                            <input
                                                type="text"
                                                value={addShare}
                                                onChange={(e) => setAddShare(e.target.value)}
                                                placeholder="0.5"
                                                style={{ ...inputStyle, width: '100%', fontFamily: 'ui-monospace, monospace' }}
                                            />
                                        </div>
                                        <button
                                            onClick={handleAddMember}
                                            disabled={saving || !addPersonId}
                                            style={{
                                                background: '#10b981', color: '#fff',
                                                border: 'none', borderRadius: 8,
                                                padding: '7px 10px', cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: 4,
                                                opacity: (saving || !addPersonId) ? 0.55 : 1,
                                            }}
                                            title="Ajouter le membre"
                                        >
                                            <UserPlus size={14} />
                                        </button>
                                    </div>
                                )}
                                {availablePersons.length === 0 && members.length > 0 && (
                                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 10, fontStyle: 'italic' }}>
                                        Toutes les personnes du projet sont déjà dans cette cohabitation.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                {isEdit && (
                    <div style={{
                        padding: '14px 22px', borderTop: `1px solid ${border}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: surfaceAlt,
                    }}>
                        <button
                            onClick={handleDelete}
                            disabled={saving}
                            style={{
                                background: 'transparent', color: '#ef4444',
                                border: `1px solid #ef444455`, borderRadius: 8,
                                padding: '7px 12px', fontSize: 12, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 6,
                            }}
                        >
                            <Trash2 size={13} /> Supprimer la cohabitation
                        </button>
                        <button
                            onClick={onClose}
                            disabled={saving}
                            style={{
                                background: 'transparent', color: textSecondary,
                                border: `1px solid ${border}`, borderRadius: 8,
                                padding: '7px 14px', fontSize: 12, cursor: 'pointer',
                            }}
                        >
                            Fermer
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const Label = ({ children, textSecondary }) => (
    <div style={{
        fontSize: 10, color: textSecondary, marginBottom: 5, fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: 0.4,
    }}>
        {children}
    </div>
);

export default CohabitationEditModal;
